import { BaseSensor, SensorHealth } from '../../types/sensor';
import { ColorMarkerConfig, TrackingResult, BoundingBox } from '../../types/tracking';
import { estimateHsvFromRoi } from '../../../utils/hsv';
import ColorWorker from '../../../workers/color-tracker.worker?worker';

export const TENNIS_BALL_PRESET: ColorMarkerConfig = {
  hsvLower: [25, 70, 70],
  hsvUpper: [75, 255, 255],
  minArea: 150, // Minimum 150 px² to reject background noise specks
  maxArea: 50000,
  minCircularity: 0.35, // Balanced threshold with Euclidean perimeter
  smoothingAlpha: 0.35,
  maxLostFrames: 30,
  downsampleWidth: 640,
};

export class ColorMarkerTrackerSensor implements BaseSensor<ColorMarkerConfig, TrackingResult> {
  readonly id: string;
  readonly name: string;

  private config: ColorMarkerConfig = { ...TENNIS_BALL_PRESET };
  private worker: Worker | null = null;
  private isProcessing = false;

  private sequence = 0;
  private lostFrameCount = 0;
  private previousFilteredPos: { x: number; y: number } | null = null;

  private callbacks: Array<(result: TrackingResult, maskImageData?: ImageData) => void> = [];
  private offscreenCanvas: HTMLCanvasElement | null = null;
  private offscreenCtx: CanvasRenderingContext2D | null = null;

  // Diagnostics & Stats
  private status: SensorHealth['status'] = 'idle';
  private lastEmitMs = 0;
  private droppedCount = 0;
  private errorCount = 0;
  private lastError?: string;
  private qualityFlags: Set<string> = new Set();

  constructor(id = 'tracker.color-marker.a', name = 'Color Tracker A') {
    this.id = id;
    this.name = name;
  }

  describe() {
    return {
      id: this.id,
      name: this.name,
      version: '1.0.0',
      description: 'HSV Color Marker Software Sensor for Tennis Ball Tracking',
    };
  }

  configure(config: Partial<ColorMarkerConfig>): void {
    this.config = { ...this.config, ...config };
  }

  getConfig(): ColorMarkerConfig {
    return { ...this.config };
  }

  resetTracking(): void {
    this.sequence = 0;
    this.lostFrameCount = 0;
    this.previousFilteredPos = null;
  }

  async start(): Promise<void> {
    this.status = 'configuring';
    this.resetTracking();

    try {
      if (typeof Worker !== 'undefined') {
        const workerInstance = new ColorWorker();
        workerInstance.onmessage = (e: MessageEvent) => {
          const { result, maskImageData } = e.data;
          this.isProcessing = false;
          this.sequence++;
          this.lastEmitMs = result.monotonicMs;

          if (result.status === 'tracking') {
            this.previousFilteredPos = {
              x: result.filteredPosition.x,
              y: result.filteredPosition.y,
            };
            this.lostFrameCount = 0;
          } else {
            this.lostFrameCount = result.lostFrameCount;
          }

          for (const cb of this.callbacks) {
            cb(result, maskImageData);
          }
        };
        this.worker = workerInstance;
      }

      this.status = 'running';
    } catch (err: any) {
      this.status = 'error';
      this.lastError = err.message || String(err);
      this.errorCount++;
      throw err;
    }
  }

  processFrame(bitmap: ImageBitmap, returnMask = false): void {
    if (this.status !== 'running' || this.isProcessing) {
      this.droppedCount++;
      bitmap.close();
      return;
    }

    const observedAt = Date.now();
    const monotonicMs = performance.now();

    const scale = Math.min(1, this.config.downsampleWidth / bitmap.width);
    const targetWidth = Math.round(bitmap.width * scale);
    const targetHeight = Math.round(bitmap.height * scale);

    if (!this.offscreenCanvas) {
      this.offscreenCanvas = document.createElement('canvas');
    }
    if (
      this.offscreenCanvas.width !== targetWidth ||
      this.offscreenCanvas.height !== targetHeight
    ) {
      this.offscreenCanvas.width = targetWidth;
      this.offscreenCanvas.height = targetHeight;
      this.offscreenCtx = this.offscreenCanvas.getContext('2d', {
        willReadFrequently: true,
      });
    }

    if (!this.offscreenCtx) {
      bitmap.close();
      return;
    }

    this.offscreenCtx.drawImage(bitmap, 0, 0, targetWidth, targetHeight);
    bitmap.close();

    const imageData = this.offscreenCtx.getImageData(0, 0, targetWidth, targetHeight);
    this.isProcessing = true;

    if (this.worker) {
      this.worker.postMessage({
        type: 'PROCESS_FRAME',
        imageData,
        config: this.config,
        sequence: this.sequence,
        observedAt,
        monotonicMs,
        cameraId: this.id,
        previousFilteredPos: this.previousFilteredPos,
        lostFrameCount: this.lostFrameCount,
        returnMask,
      });
    }
  }

  estimateHsvFromSelectedRoi(bitmap: ImageBitmap, roi: BoundingBox): { lower: [number, number, number]; upper: [number, number, number] } {
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return { lower: this.config.hsvLower, upper: this.config.hsvUpper };

    ctx.drawImage(bitmap, 0, 0);
    const imageData = ctx.getImageData(0, 0, bitmap.width, bitmap.height);
    const bounds = estimateHsvFromRoi(imageData, roi);
    this.configure({ hsvLower: bounds.lower, hsvUpper: bounds.upper });
    return bounds;
  }

  async stop(): Promise<void> {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    this.status = 'stopped' as any;
    this.isProcessing = false;
  }

  health(): SensorHealth {
    return {
      status: this.status,
      lastEmitMonotonicMs: this.lastEmitMs,
      droppedCount: this.droppedCount,
      errorCount: this.errorCount,
      lastErrorMessage: this.lastError,
      qualityFlags: Array.from(this.qualityFlags),
    };
  }

  onData(callback: (result: TrackingResult, maskImageData?: ImageData) => void): void {
    this.callbacks.push(callback);
  }
}
